import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { trackPageView } from '../../lib/gaEvents';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { Box, Divider, Grid, Stack, useMediaQuery } from '@mui/material';
import theme from '../../theme';
import PlaylistVerticalCard from '../../components/card/PlaylistVerticalCard';
import PlaylistCard from '../../components/card/PlaylistCard';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import InFeedAd from '../../components/ad/InFeedAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { playlistApi } from '../../api/playlist';
import SelectFilter from '../../components/common/SelectFilter';
import SubTab from '../../components/button/SubTab';
import PageLayout from '../../components/layout/PageLayout';
import PageTitle from '../../components/common/PageTitle';
import NoData from '../../components/common/NoData';
import { useUserStore } from '../../stores/userStore';
import CreateButton from '../../components/button/CreateButton';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import SEO from '../../components/common/SEO';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const DEFAULT_SORT = 'latest';
const DEFAULT_TYPE = 'songs';
const SORT_OPTIONS = ['latest', 'popular'];
const TYPE_OPTIONS = ['songs', 'albums'];

const SUB_TABS = [
  { label: '곡 플리', key: 'songs' },
  { label: '앨범 플리', key: 'albums' },
];

// subTab에 따른 API 매핑
const apiMap = {
  songs: playlistApi.getTrackPlaylists,
  albums: playlistApi.getAlbumPlaylists,
};

// 5줄마다 배너
const ITEMS_PER_AD = { xs: 5, md: 20 };

// 10줄마다 페이지네이션 (xs: 1열, md/lg: 4열)
const ITEMS_PER_PAGE = { xs: 10, md: 40 };

const Playlists = () => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(() => {
    const urlSort = searchParams.get('sort');
    return SORT_OPTIONS.includes(urlSort) ? urlSort : DEFAULT_SORT;
  });
  const [typeTab, setTypeTab] = useState(() => {
    const urlType = searchParams.get('type');
    return TYPE_OPTIONS.includes(urlType) ? urlType : DEFAULT_TYPE;
  });
  const listContentType = typeTab === 'albums' ? CONTENT_TYPE.LIST_ALBUM : CONTENT_TYPE.LIST_SONG;
  useTrackPageView({ pageType: PAGE_TYPE.LISTS_MAIN, contentType: listContentType });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: null,
    totalItems: null,
    itemsPerPage: 40,
  });

  // breakpoint 감지
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const itemsPerPage = isXs ? ITEMS_PER_PAGE.xs : ITEMS_PER_PAGE.md;
  const itemsPerAd = isXs ? ITEMS_PER_AD.xs : ITEMS_PER_AD.md;

  const fetchPlaylists = async (type, sortBy, page = 1, size) => {
    setLoading(true);
    try {
      const apiFn = apiMap[type];
      const response = await apiFn(sortBy, page, size);
      setPlaylists(response.data);
      if (response.meta && response.meta.pagination) {
        setPagination(response.meta.pagination);
      }
    } catch (err) {
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    const urlSort = searchParams.get('sort');
    const urlType = searchParams.get('type');
    const currentSort = SORT_OPTIONS.includes(urlSort) ? urlSort : DEFAULT_SORT;
    const currentType = TYPE_OPTIONS.includes(urlType) ? urlType : DEFAULT_TYPE;
    setSortBy(currentSort);
    setTypeTab(currentType);
    fetchPlaylists(currentType, currentSort, page, itemsPerPage);
  }, [searchParams, itemsPerPage]);

  const handleTypeTabChange = (newType) => {
    const newContentType = newType === 'albums' ? CONTENT_TYPE.LIST_ALBUM : CONTENT_TYPE.LIST_SONG;
    trackPageView({ pageType: PAGE_TYPE.LISTS_MAIN, contentType: newContentType }, user);
    setSearchParams({ type: newType, sort: DEFAULT_SORT, page: '1' });
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setSearchParams({ type: typeTab, sort: newSort, page: '1' });
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams({ type: typeTab, sort: sortBy, page: newPage.toString() });
  };

  const handleOnClickCreate = () => {
    if (user === null || Object.keys(user).length === 0) {
      saveCurrentPathAndRedirectToLogin(navigate);
    } else {
      navigate('/lists/create?from=list');
    }
  };

  return (
    <PageLayout>
      <SEO title="플리" description={SEO_DESCRIPTIONS.LISTS} url={`${SITE_URL}/lists`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: '플리' },
            ],
          })}
        </script>
      </Helmet>
      {playlists.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: playlists
                .filter((item) => item.imageUrl)
                .map((item, index) => ({
                  '@type': 'ListItem',
                  name: item.title,
                  image: item.imageUrl,
                  url: item.slug
                    ? `${SITE_URL}/lists/${item.slug}`
                    : `${SITE_URL}/lists/${item.id}`,
                  position: index + 1,
                })),
            })}
          </script>
        </Helmet>
      )}
      {/* 타이틀 + 만들기 버튼 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '16px', md: '20px', lg: '30px' },
          mb: { xs: '30px', lg: '40px' },
        }}
      >
        <PageTitle>플리</PageTitle>
        <CreateButton
          text="만들기"
          onClick={handleOnClickCreate}
          sx={{ fontSize: 14, fontWeight: 500, padding: { xs: '10px 12px', md: '12px 16px' } }}
        />
      </Box>
      <SidebarLayout pageType={PAGE_TYPE.LISTS_MAIN}>
        {/* 서브탭 + 필터 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: '16px', md: '20px', lg: '30px' },
          }}
        >
          <SubTab tabs={SUB_TABS} activeTab={typeTab} onTabChange={handleTypeTabChange} />
          <SelectFilter
            label="정렬"
            value={sortBy}
            onChange={handleSortChange}
            options={[
              { label: '최신순', value: 'latest' },
              { label: '인기순', value: 'popular' },
            ]}
          />
        </Box>

        <ProgressBar isLoading={loading}>
          {playlists.length === 0 ? (
            <NoData />
          ) : (
            <>
              {isXs ? (
                <Stack spacing="15px">
                  {playlists.map((playlist, index) => (
                    <React.Fragment key={`${playlist.id}-${index}`}>
                      <Box>
                        <PlaylistCard
                          playlist={playlist}
                          dateOnNewLine
                          thumbnailVariant="square"
                        />
                        <Divider sx={{ borderColor: theme.palette.grey.line, mt: '15px' }} />
                      </Box>
                      {(index + 1) % itemsPerAd === 0 && index < playlists.length - 1 && (
                        <InFeedAd pageType={PAGE_TYPE.LISTS_MAIN} />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ overflow: 'hidden' }}>
                  <Grid container columnSpacing={{ md: '16px', lg: '20px' }} rowSpacing={'40px'}>
                    {playlists.map((playlist, index) => (
                      <React.Fragment key={`${playlist.id}-${index}`}>
                        <Grid item xs={12} md={3}>
                          <PlaylistVerticalCard playlist={playlist} thumbnailVariant="square" />
                        </Grid>
                        {(index + 1) % itemsPerAd === 0 && index < playlists.length - 1 && (
                          <Grid item xs={12}>
                            <InFeedAd pageType={PAGE_TYPE.LISTS_MAIN} />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}
                  </Grid>
                </Box>
              )}
              {/*페이지네이션*/}
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
              />
            </>
          )}
        </ProgressBar>
      </SidebarLayout>
    </PageLayout>
  );
};

export default Playlists;
